import { Menu, MenuOpen, ArrowBack } from '@mui/icons-material'
import { Box, IconButton, Stack } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { AppSidebarContent } from './AppSidebarContent'
import { useOriginalPath } from './hooks/useOriginalPath'
import { SettingsSidebarContent } from './settings/SettingsSidebarContent'
import { SidebarPages } from './SidebarPages'

export const SIDEBAR_WIDTH = 240

export type MenuItemType = {
  type: SidebarPages
  title: string
  Icon: React.ElementType
  href: string
  selected: boolean
  count?: number
  /** Label pill rendered next to the title, e.g. "New". Not rendered while the sidebar is collapsed. */
  badge?: string
  children?: Array<MenuItemType>
}

export interface SidebarUncontrolledProps {
  isSidebarExpanded: boolean
  items: MenuItemType[]
  selectedPage: SidebarPages
  onToggleExpanded: (value: boolean) => void
  numberOfActiveTasks: number
  isSettingsDashboard?: boolean
  path: string | null
}

export const SidebarUncontrolled = ({
  isSidebarExpanded,
  selectedPage,
  items,
  onToggleExpanded,
  numberOfActiveTasks,
  path,
}: SidebarUncontrolledProps) => {
  const { t } = useTranslation('common')
  const isSettingsDashboard = !!path?.includes('/settings')
  const backToAppPath = useOriginalPath(isSettingsDashboard)

  useEffect(() => {
    if (isSettingsDashboard && !isSidebarExpanded) {
      onToggleExpanded(true)
    }
  }, [isSettingsDashboard, isSidebarExpanded, onToggleExpanded])

  return (
    <Stack
      justifyContent="space-between"
      sx={{
        bgcolor: 'bg.surface.default',
        width: isSidebarExpanded ? SIDEBAR_WIDTH : 70,
        borderRight: 'stroke.default',
        borderRightColor: 'border.subtle',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        data-testid="sidebar-header"
        sx={{
          minHeight: 51,
          backgroundColor: 'bg.surface.default',
          borderBottom: 'stroke.default',
          borderColor: 'border.subtle',
          px: 2,
          ...(isSettingsDashboard && {
            '& > a': {
              ml: -2,
            },
          }),
        }}
      >
        {isSettingsDashboard ? (
          <Link href={backToAppPath} passHref style={{ textDecoration: 'none', width: '100%' }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                color: 'text.muted',
                py: 1,
                px: 6,
                '&:hover': {
                  color: 'text.default',
                },
              }}
            >
              <ArrowBack sx={{ fontSize: 14 }} />
              <Box component="span" sx={{ typography: 'body-regular', fontWeight: 400 }}>
                {t('sidebar.back-to-app')}
              </Box>
            </Stack>
          </Link>
        ) : (
          <>
            {isSidebarExpanded && (
              <Box sx={{ cursor: 'pointer', pt: 1, pl: 4 }}>
                <Link href="/" passHref>
                  <Image src="/logo-dark.svg" alt="Omnea" height={18} width={99} priority />
                </Link>
              </Box>
            )}
            <IconButton
              onClick={() => onToggleExpanded(!isSidebarExpanded)}
              sx={{ ...(isSidebarExpanded ? {} : { mx: 'auto', width: '100%', borderRadius: 1 }) }}
            >
              {isSidebarExpanded ? <MenuOpen sx={{ width: 18 }} /> : <Menu sx={{ width: 18 }} />}
            </IconButton>
          </>
        )}
      </Stack>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: (theme) => theme.palette.bg.neutral.strong,
            borderRadius: 8,
          },
        }}
      >
        {isSettingsDashboard ? (
          <SettingsSidebarContent />
        ) : (
          <AppSidebarContent
            isSidebarExpanded={isSidebarExpanded}
            selectedPage={selectedPage}
            items={items}
            numberOfActiveTasks={numberOfActiveTasks}
            onToggleExpanded={onToggleExpanded}
            isSettingsDashboard={isSettingsDashboard}
          />
        )}
      </Box>
    </Stack>
  )
}
